import queryString from 'query-string';

import type { HistoryType } from 'jsx/types/routing';

/**
 * `react-router-dom@5.2.0` separated history into its own context to reduce re-renders.
 * As a result, if you *need* a component to re-render based on location state changing,
 * you should use `useLocation` instead of `useHistory`, and import `parseQuery` directly
 * instead of relying on `getQueryObject`.
 *
 * {@link https://github.com/ReactTraining/react-router/pull/7103 History Context PR}
 */
export function parseQuery(query: string) {
  return queryString.parse(query, {
    arrayFormat: 'bracket',
  });
}

export function getQueryObject(
  historyOrWindow?: HistoryType | Window,
): queryString.ParsedQuery | null {
  if (historyOrWindow) {
    return parseQuery(historyOrWindow.location.search);
  }

  return null;
}

/**
 * @deprecated this function does not support nesting and will silently coerce
 * any nested objects to `[object Object]`. Use the QueryParamsEncoder from
 * `jsx/utils/queryParams` instead.
 */
export function objectToQueryString(obj: Record<string, unknown>): string {
  return queryString.stringify(obj, { arrayFormat: 'bracket' });
}

function buildNewQueryString(
  history: HistoryType,
  newQuery: Record<string, unknown>,
): string {
  const obj = { ...getQueryObject(history), ...newQuery };
  return objectToQueryString(obj);
}

export function setQueryObject(
  history: HistoryType,
  newQuery: Record<string, unknown>,
): void {
  const params = buildNewQueryString(history, newQuery);

  history.push({
    pathname: history.location.pathname,
    search: `?${params}`,
  });
}

export function replaceQueryObject(
  history: HistoryType,
  newQuery: Record<string, unknown>,
): void {
  const params = buildNewQueryString(history, newQuery);

  history.replace({
    pathname: history.location.pathname,
    search: `?${params}`,
  });
}

export function removeQueryStringFromUrl(
  history: HistoryType | undefined,
): void {
  if (history) {
    history.push(history.location.pathname);
  }
}
